import type { StudentTranscriptRow } from '../services/api';

/**
 * Transcript review status — derived from whether the partner's team has downloaded a
 * student's transcripts, relative to the student's latest certified course.
 *
 * The labels and descriptions here are PARTNER-FACING (shown on the Transcripts page chip
 * + tooltip and in the Review Status filter). Keep them in sync with the meaning below:
 *  - needs_review ("Awaiting Review") — no download recorded for any of the student's transcripts.
 *  - updated ("Modified") — an older transcript was downloaded, but the latest one was not
 *    (i.e. the student has completed new courses since the team last downloaded).
 *  - reviewed ("Reviewed") — the latest transcript has been downloaded; nothing new since.
 */
export type ReviewStatus = 'reviewed' | 'updated' | 'needs_review';

export const ALL_REVIEW_STATUSES: ReviewStatus[] = ['needs_review', 'updated', 'reviewed'];

type MyDownloads = Record<number, { downloaded_at: string; download_origin?: string }>;
type OrgDownloads = Record<
  number,
  { user_email: string; downloaded_at: string; download_origin?: string }
>;

/**
 * A transcript certified strictly before the partner's Portal launch date was
 * sent to the partner by email under the pre-Portal process, so it counts as
 * already reviewed (SOPHIA-35018). Certification dates are ISO timestamps;
 * launch date is YYYY-MM-DD, so a date-prefix string compare is exact.
 */
export function isPreLaunch(
  certificationDate: string | null | undefined,
  portalLaunchDate: string | null | undefined
): boolean {
  if (!certificationDate || !portalLaunchDate) return false;
  return certificationDate.slice(0, 10) < portalLaunchDate;
}

export function getReviewStatus(
  student: StudentTranscriptRow,
  myDownloads: MyDownloads,
  orgDownloads: OrgDownloads,
  portalLaunchDate?: string | null
): ReviewStatus {
  if (student.transcript_ids.length === 0) return 'needs_review';
  const latestId = student.latest_transcript_id;
  const latestDownloaded = !!myDownloads[latestId] || !!orgDownloads[latestId];
  if (latestDownloaded) return 'reviewed';
  if (isPreLaunch(student.latest_certification_date, portalLaunchDate)) return 'reviewed';
  const anyDownloaded = student.transcript_ids.some(
    (id) => !!myDownloads[id] || !!orgDownloads[id]
  );
  if (anyDownloaded || isPreLaunch(student.earliest_certification_date, portalLaunchDate)) {
    return 'updated';
  }
  return 'needs_review';
}

/**
 * Course-level NEW chip (SOPHIA-35246). While the learner still has unreviewed
 * work (Awaiting Review or Modified), each course counts as NEW until someone
 * on the team downloads its transcript — except courses covered by the
 * pre-launch auto-review rule, which are never NEW. The learner-level NEW chip
 * is simply `getReviewStatus(...) === 'needs_review'` (no downloads at all and
 * nothing explained by the launch date), so it needs no helper.
 */
export function isCourseNew(
  learnerStatus: ReviewStatus,
  transcriptId: number,
  certificationDate: string | null | undefined,
  myDownloads: MyDownloads,
  orgDownloads: OrgDownloads,
  portalLaunchDate?: string | null
): boolean {
  if (learnerStatus === 'reviewed') return false;
  if (myDownloads[transcriptId] || orgDownloads[transcriptId]) return false;
  return !isPreLaunch(certificationDate, portalLaunchDate);
}

/**
 * True when a student reads as Reviewed only because their whole (visible)
 * history predates the Portal launch date — no one actually downloaded the
 * latest transcript. Used to show an accurate tooltip on the status chip.
 */
export function isPreLaunchReviewed(
  student: StudentTranscriptRow,
  myDownloads: MyDownloads,
  orgDownloads: OrgDownloads,
  portalLaunchDate?: string | null
): boolean {
  if (student.transcript_ids.length === 0) return false;
  const latestId = student.latest_transcript_id;
  const latestDownloaded = !!myDownloads[latestId] || !!orgDownloads[latestId];
  return !latestDownloaded && isPreLaunch(student.latest_certification_date, portalLaunchDate);
}

export const preLaunchReviewedDescription =
  "This student's transcripts were certified before your institution's Portal launch date " +
  'and were previously sent by email, so they are marked as reviewed.';

export const reviewStatusConfig: Record<
  ReviewStatus,
  { label: string; color: string; bg: string; description: string }
> = {
  reviewed: {
    label: 'Reviewed',
    color: '#2E7D32',
    bg: '#E8F5E9',
    description:
      "Your team has downloaded this student's latest transcript. No course completions since.",
  },
  updated: {
    label: 'Modified',
    color: '#E65100',
    bg: '#FFF3E0',
    description:
      'This student has completed new courses since your team last downloaded their transcript. Download again to get the latest record.',
  },
  needs_review: {
    label: 'Awaiting Review',
    color: '#250e62',
    bg: '#EDE7F6',
    description: "No one on your team has downloaded this student's transcript yet.",
  },
};
